import React, { createContext, useContext, useMemo, useCallback, useState } from "react";
import PropTypes from "prop-types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchFleetSearchDetails } from "../../pages/fleet_metrics/hooks/useFleetSearchHook/api";
import { queryKeys } from "../query";
const FleetDataContext = createContext(null);

export const FleetDataProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const [requested, setRequested] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.fleet.list(),
    queryFn: async () => {
      const response = await fetchFleetSearchDetails();
      return response?.data ?? [];
    },
    staleTime: 5 * 60 * 1000,
    enabled: requested, // Only fetches when a consumer signals it needs the data
  });

  // Maintain backward-compatible state shape: { isLoading, isError, data }
  const state = useMemo(
    () => ({
      isLoading,
      isError,
      data: data ?? [],
    }),
    [data, isLoading, isError]
  );

  const refresh = useCallback(() => {
    setRequested(true);
    return queryClient.invalidateQueries({ queryKey: queryKeys.fleet.list() });
  }, [queryClient]);

  // Signal that a consumer needs fleet data (triggers fetch on first call)
  const requestFetch = useCallback(() => setRequested(true), []);

  const value = useMemo(() => ({ state, refresh, requestFetch }), [state, refresh, requestFetch]);

  return <FleetDataContext.Provider value={value}>{children}</FleetDataContext.Provider>;
};

FleetDataProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useFleetData() {
  const ctx = useContext(FleetDataContext);
  if (!ctx) throw new Error("useFleetData must be used within FleetDataProvider");
  return ctx;
}

/**
 * Imperative getter for ev_charger data — reads from React Query cache
 * or fetches if stale / missing.  Use in useEffect or async functions
 * where hooks cannot be called.
 *
 * @param {import("@tanstack/react-query").QueryClient} queryClient
 * @returns {Promise<Array>} Array of charger objects
 */
export async function fetchFleetDataCached(queryClient) {
  return queryClient.fetchQuery({
    queryKey: queryKeys.fleet.list(),
    queryFn: async () => {
      const response = await fetchFleetSearchDetails();
      return response?.data ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export default FleetDataContext;
